import { useEffect, useId, useRef, useState } from 'react';
import { Gauge, LoaderCircle } from 'lucide-react';
import type { ExtractionPreference } from '../shared/desktop-api';
import './processing-mode.css';

const choices = [
  { value: 'auto', title: 'Automatic' },
  { value: 'faster', title: 'Prioritize speed' },
  { value: 'higher-accuracy', title: 'Prioritize accuracy' },
] as const;

/** The save that failed; every other message here is the choice that could not be read. */
const CHOICE_NOT_SAVED =
  'Could not save that choice. Your previous setting is unchanged; try again.';

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function ExtractionSettings() {
  const id = useId();
  const [selected, setSelected] = useState<ExtractionPreference | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const live = useRef(false);
  const pending = useRef(false);
  useEffect(() => {
    let current = true;
    live.current = true;
    const api = window.capturePointDesktop;
    if (!api) setError('Processing settings are unavailable in this view.');
    else
      void api
        .getExtractionPreference()
        .then((value) => {
          if (current) setSelected(value);
        })
        .catch(() => {
          if (current) setError('Could not load your choice. Choose an option to save it again.');
        });
    return () => {
      current = false;
      live.current = false;
    };
  }, []);
  async function choose(value: ExtractionPreference) {
    const api = window.capturePointDesktop;
    if (!api || pending.current || (selected === null && !error) || value === selected) return;
    pending.current = true;
    setSaving(true);
    setError(null);
    try {
      const saved = await api.setExtractionPreference(value);
      if (live.current) setSelected(saved);
    } catch {
      if (live.current) setError(CHOICE_NOT_SAVED);
    } finally {
      pending.current = false;
      if (live.current) setSaving(false);
    }
  }
  return (
    <section className="extraction-settings processing-mode">
      <label htmlFor={id}>
        <Gauge size={16} aria-hidden="true" /> Processing mode
      </label>
      <select
        id={id}
        value={selected ?? ''}
        disabled={saving || (selected === null && !error)}
        aria-describedby={`${id}-help${error ? ` ${id}-error` : ''}`}
        onChange={(event) => void choose(event.target.value as ExtractionPreference)}
      >
        {selected === null && (
          <option value="" disabled>
            {error ? 'Choose a mode' : saving ? 'Saving…' : 'Loading…'}
          </option>
        )}
        {choices.map(({ value, title }) => (
          <option key={value} value={value}>
            {title}
          </option>
        ))}
      </select>
      <p id={`${id}-help`} className="processing-mode-help">
        For all jobs on this computer. Applies to documents processed next.
      </p>
      {selected !== null && selected !== 'auto' && (
        <p className="processing-mode-test-note">
          The speed test always runs on Automatic, so its score compares.
        </p>
      )}
      {saving && (
        <p role="status">
          <LoaderCircle size={15} className="spin" aria-hidden="true" /> Saving…
        </p>
      )}
      {selected === null && !error && !saving && <p role="status">Loading your saved choice…</p>}
      {error && (
        <p
          id={`${id}-error`}
          role="alert"
          data-telemetry={
            error === CHOICE_NOT_SAVED
              ? 'error:processing-mode-save-failed'
              : 'error:processing-mode-load-failed'
          }
        >
          {error}
        </p>
      )}
    </section>
  );
}
